"use client";

import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { ActivityCommentLikeListItem } from "@/actions/activity-post";
import type { ActivitySessionCommentLikeUser } from "@/actions/activity-session-interaction";
import type { CommentLikeListItem } from "@/actions/post";
import type { SocialFeedCommentLikeUser } from "@/actions/social-feed-interaction";
import { ConnectButton } from "@/components/buddy";
import { ProfileLink } from "@/components/profile/profile-link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import type { SocialFeedEntity } from "@/db/schema/social-feed";
import { useActivityCommentLikes } from "@/hooks/use-activity-comments";
import { useActivitySessionCommentLikes } from "@/hooks/use-activity-session-comments";
import { useBuddyRelationships } from "@/hooks/use-buddy-relationships";
import { useCommentLikes } from "@/hooks/use-posts";
import { useSocialFeedCommentLikes } from "@/hooks/use-social-feed-interactions";
import type { BuddyRelationship } from "@/lib/buddy/relationship";
import { formatAbsoluteDay } from "@/lib/dates/relative-time";

type EntityType = "post" | "activity" | "activity-session" | "social";

interface CommentLikesSheetProps {
  entityType?: EntityType;
  commentId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
  socialEntityType?: SocialFeedEntity;
  layer?: number;
}

// Unified like type for UI
type UILike = {
  id: string;
  displayName: string;
  username: string | null;
  avatarUrl: string | null;
  likedAt: Date | number;
  isBuddy?: boolean;
  isBlocked?: boolean;
};

export function CommentLikesSheet(props: CommentLikesSheetProps) {
  const { open, onOpenChange, currentUserId, commentId, layer } = props;
  const entityType: EntityType = props.entityType ?? "post";
  const socialEntityType =
    entityType === "social" ? props.socialEntityType : null;

  const [query, setQuery] = useState("");

  const likesEnabled = open && commentId > 0;

  // Use appropriate hooks based on entity type
  const postLikesQuery = useCommentLikes(
    entityType === "post" ? commentId : null,
    { enabled: likesEnabled && entityType === "post" },
  );
  const activityLikesQuery = useActivityCommentLikes(
    entityType === "activity" ? commentId : null,
    { enabled: likesEnabled && entityType === "activity" },
  );
  const activitySessionLikesQuery = useActivitySessionCommentLikes(
    entityType === "activity-session" ? commentId : null,
    { enabled: likesEnabled && entityType === "activity-session" },
  );
  const socialLikesQuery = useSocialFeedCommentLikes(commentId, {
    enabled:
      likesEnabled && entityType === "social" && Boolean(socialEntityType),
  });

  const likesQuery =
    entityType === "activity"
      ? activityLikesQuery
      : entityType === "activity-session"
        ? activitySessionLikesQuery
        : entityType === "social"
          ? socialLikesQuery
          : postLikesQuery;

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = likesQuery;

  // Transform to unified format
  const likes = ((): UILike[] => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.likes.map((like): UILike => {
        if (entityType === "post") {
          const postLike = like as CommentLikeListItem;
          return {
            id: postLike.id,
            displayName: postLike.displayName,
            username: postLike.username,
            avatarUrl: postLike.avatarUrl,
            likedAt: postLike.likedAt,
            isBuddy: postLike.id === currentUserId ? false : postLike.isBuddy,
            isBlocked: Boolean(postLike.isBlocked),
          };
        }
        if (entityType === "activity") {
          const activityLike = like as ActivityCommentLikeListItem;
          return {
            id: activityLike.id,
            displayName: activityLike.displayName,
            username: activityLike.username,
            avatarUrl: activityLike.avatarUrl,
            likedAt: activityLike.likedAt,
            isBuddy: undefined,
            isBlocked: Boolean(activityLike.isBlocked),
          };
        }
        if (entityType === "social") {
          const socialLike = like as SocialFeedCommentLikeUser;
          return {
            id: socialLike.userId,
            displayName: socialLike.displayName,
            username: socialLike.username,
            avatarUrl: socialLike.avatarUrl,
            likedAt: socialLike.likedAt,
            isBuddy: undefined,
            isBlocked: false,
          };
        }
        // activity-session
        const activitySessionLike = like as ActivitySessionCommentLikeUser;
        return {
          id: activitySessionLike.userId,
          displayName: activitySessionLike.displayName,
          username: activitySessionLike.username,
          avatarUrl: activitySessionLike.avatarUrl,
          likedAt: activitySessionLike.likedAt,
          isBuddy: undefined,
          isBlocked: false,
        };
      }),
    );
  })();

  useEffect(() => {
    if (!error) return;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Failed to load likes";
    toast.error(message);
  }, [error]);

  // Same as the post likes sheet: these endpoints do not carry pending-request
  // state, so the quick-add button resolves it in one call for the page.
  const { relationshipOf } = useBuddyRelationships(
    likes.map((like) => like.id),
  );

  const filteredLikes = (() => {
    if (!query) {
      return likes;
    }
    const lowered = query.toLowerCase();
    return likes.filter((item) => {
      const name = item.displayName?.toLowerCase() ?? "";
      const username = item.username?.toLowerCase() ?? "";
      return name.includes(lowered) || username.includes(lowered);
    });
  })();

  return (
    <Drawer open={open} onOpenChange={onOpenChange} nested>
      <DrawerContent
        layer={layer}
        className="mx-auto w-full max-w-lg rounded-t-[28px] border-border bg-background shadow-2xl"
      >
        <DrawerHeader className="pb-0 text-center">
          <DrawerTitle className="text-base font-semibold text-text-primary">
            Likes
          </DrawerTitle>
        </DrawerHeader>

        <div className="space-y-2 px-4 py-4">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-text-secondary" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              className="pl-9"
            />
          </div>
        </div>

        <Separator className="bg-border" />

        <div className="no-scrollbar flex h-[50vh] flex-col gap-4 overflow-y-auto px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {commentId < 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-text-secondary">
              <p className="text-sm">
                Likes will appear once the comment is live.
              </p>
            </div>
          ) : isLoading ? (
            <div className="flex flex-1 items-center justify-center text-text-secondary">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filteredLikes.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-text-secondary">
              <p className="text-sm">
                {likes.length === 0
                  ? "No likes yet."
                  : "No matches for your search."}
              </p>
            </div>
          ) : (
            filteredLikes.map((like) => (
              <LikeRow
                key={like.id}
                like={like}
                isOwnLike={like.id === currentUserId}
                relationship={relationshipOf(like.id)}
              />
            ))
          )}

          {hasNextPage && commentId > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="mx-auto w-fit text-text-secondary"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading…" : "View more"}
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface LikeRowProps {
  like: UILike;
  isOwnLike?: boolean;
  relationship: BuddyRelationship | undefined;
}

function LikeRow({ like, isOwnLike, relationship }: LikeRowProps) {
  const isBlocked = Boolean(like.isBlocked);
  const secondary = like.username
    ? `@${like.username}`
    : isBlocked
      ? "Blocked profile"
      : formatRelative(like.likedAt);

  return (
    <div className="flex items-center justify-between gap-3">
      {isBlocked ? (
        <div className="flex flex-1 items-center gap-3">
          <Avatar className="h-10 w-10 border border-border">
            <AvatarImage src={undefined} />
            <AvatarFallback>
              {initials(like.displayName || like.username || "")}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-text-primary">
              {like.displayName || like.username}
            </span>
            <span className="text-xs text-text-secondary">{secondary}</span>
          </div>
        </div>
      ) : (
        <ProfileLink
          handle={like.username}
          fallbackAs="div"
          className="flex flex-1 items-center gap-3 transition-opacity hover:opacity-80"
        >
          <Avatar className="h-10 w-10 border border-border">
            <AvatarImage src={like.avatarUrl ?? undefined} />
            <AvatarFallback>
              {initials(like.displayName || like.username || "")}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-text-primary">
              {like.displayName || like.username}
            </span>
            <span className="text-xs text-text-secondary">{secondary}</span>
          </div>
        </ProfileLink>
      )}

      {!isBlocked && !isOwnLike && (
        <ConnectButton
          targetUserId={like.id}
          targetName={like.displayName || like.username}
          relationship={relationship}
          surface="comment_likes"
          buddyFallback={
            <Badge variant="outline" className="border-success text-success">
              Buddies
            </Badge>
          }
        />
      )}
    </div>
  );
}

const initials = (value: string) => {
  const cleaned = value.trim();
  if (!cleaned) {
    return "";
  }
  const parts = cleaned.split(" ");
  if (parts.length === 1) {
    return parts[0]?.slice(0, 2).toUpperCase() ?? "";
  }
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
};

const formatRelative = (value: Date | number) => formatAbsoluteDay(value);
